import React, { useEffect, useMemo } from 'react';
import { Badge, Button, ButtonGroup, Card, Col, Row } from 'react-bootstrap';
import { subscribeToTickerUpdates } from '../services/socketService';
import TickerUpdateStatus from './TickerUpdateStatus';

/**
 * Live monitor for ticker update channels (WebSocket + Polling)
 * - Shows status badges and counters
 * - Displays polling status details
 * - Renders a compact live event log (last N real updates)
 * - Provides polling controls (start/stop/force/enable)
 */
const TickerUpdateMonitor = ({
  show = false,
  isWebSocketConnected,
  isPollingActive,
  lastUpdateSource,
  totalUpdates,
  webSocketUpdates,
  pollingUpdates,
  lastUpdateTime,
  pollingStatus,
  eventLogSize = 25,
  pollingIntervalOptions = [],
  selectedIntervalSeconds,
  onPollingIntervalChange
}) => {

  // Subscribe only when the modal is shown
  useEffect(() => {
    if (!show) return;

    const unsubscribe = subscribeToTickerUpdates((data) => {
      try {
        const count = Array.isArray(data?.updated_tickers) ? data.updated_tickers.length : 0;
        if (count <= 0) return; // Only real ticker updates
      } catch {}
    });

    return () => unsubscribe();
  }, [show, eventLogSize]);

  const formatLocal = (d) => {
    if (!d) return '—';
    const date = d instanceof Date ? d : new Date(d);
    if (isNaN(date)) return '—';
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
  };

  const pollingDetails = useMemo(() => ({
    intervalMs: pollingStatus?.pollingIntervalMs,
    lastPollTime: pollingStatus?.lastPollTime ? new Date(pollingStatus.lastPollTime) : null,
    lastKnownUpdateTime: pollingStatus?.lastKnownUpdateTime ? new Date(pollingStatus.lastKnownUpdateTime) : null,
    consecutiveEmptyPolls: pollingStatus?.consecutiveEmptyPolls ?? 0,
    isEnabled: pollingStatus?.isEnabled ?? true,
    subscriberCount: pollingStatus?.subscriberCount ?? 0
  }), [pollingStatus]);

  const activeIntervalSeconds = selectedIntervalSeconds ?? (pollingDetails.intervalMs ? Math.round(pollingDetails.intervalMs / 1000) : null);
  const canChangeInterval = typeof onPollingIntervalChange === 'function';

  return (
    <div className="d-flex flex-column gap-3">
      <TickerUpdateStatus
        isWebSocketConnected={isWebSocketConnected}
        isPollingActive={isPollingActive}
        lastUpdateSource={lastUpdateSource}
        totalUpdates={totalUpdates}
        webSocketUpdates={webSocketUpdates}
        pollingUpdates={pollingUpdates}
        lastUpdateTime={lastUpdateTime}
      />

      <Card style={{ fontSize: '0.875rem' }}>
        <Card.Header className="py-2">
          <div className="d-flex align-items-center justify-content-between">
            <span>Polling Status</span>
            <Badge bg={isPollingActive ? 'info' : 'secondary'}>{isPollingActive ? 'Active' : 'Inactive'}</Badge>
          </div>
        </Card.Header>
        <Card.Body className="py-2">
          <Row className="g-3">
            {/* Left Column: Polling Configuration */}
            <Col md={6}>
              <div className="d-flex flex-column gap-3">
                <div>
                  <h6 className="text-muted mb-2">Configuration</h6>
                  <div className="mb-2">
                    <div className="d-flex align-items-center justify-content-between">
                      <span className="fw-medium">Interval:</span>
                      <span className="fw-semibold">{pollingDetails.intervalMs ? `${Math.round(pollingDetails.intervalMs / 1000)}s` : '—'}</span>
                    </div>
                  </div>
                  <div className="mb-2">
                    <div className="d-flex align-items-center justify-content-between">
                      <span className="fw-medium">Empty Polls:</span>
                      <span className="fw-semibold">{pollingDetails.consecutiveEmptyPolls}</span>
                    </div>
                  </div>
                  <div>
                    <div className="d-flex align-items-center justify-content-between">
                      <span className="fw-medium">Subscribers:</span>
                      <span className="fw-semibold">{pollingDetails.subscriberCount}</span>
                    </div>
                  </div>
                </div>
              </div>
            </Col>

            {/* Right Column: Polling Activity */}
            <Col md={6}>
              <div className="d-flex flex-column gap-3">
                <div>
                  <h6 className="text-muted mb-2">Activity</h6>
                  <div className="mb-2">
                    <div className="d-flex align-items-center justify-content-between">
                      <span className="fw-medium">Last Poll:</span>
                      <span className="fw-semibold small">{formatLocal(pollingDetails.lastPollTime)}</span>
                    </div>
                  </div>
                  <div>
                    <div className="d-flex align-items-center justify-content-between">
                      <span className="fw-medium">Last Update:</span>
                      <span className="fw-semibold small">{formatLocal(pollingDetails.lastKnownUpdateTime)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {Array.isArray(pollingIntervalOptions) && pollingIntervalOptions.length > 0 && (
        <Card style={{ fontSize: '0.875rem' }}>
          <Card.Header className="py-2">
            <span>Polling Interval</span>
          </Card.Header>
          <Card.Body className="py-3">
            <div className="d-flex flex-column align-items-center gap-2">
              <div className="text-muted small text-center">Choose how often the polling channel runs</div>
              <ButtonGroup aria-label="Polling interval selection">
                {pollingIntervalOptions.map(option => (
                  <Button
                    key={option}
                    size="sm"
                    variant={option === activeIntervalSeconds ? 'primary' : 'outline-secondary'}
                    onClick={() => canChangeInterval && onPollingIntervalChange(option)}
                    disabled={!canChangeInterval}
                    style={{ minWidth: '70px' }}
                  >
                    {option}s
                  </Button>
                ))}
              </ButtonGroup>
            </div>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default TickerUpdateMonitor;


